import React, { useState, useEffect } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Card, Button, makeStyles, useMediaQuery } from '@material-ui/core';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import cx from 'classnames';

import Api from '../../api';
import { downloadFile } from '../../services/download-file/download-file';
import sanitizer from '../../services/sanitize-service';
import allActions from '../../store/actions';
import buildPATHS from '../../utils/paths';
import { BACKEND_RESPONSE_RESULT_SCREENS } from '../../constants/backendResponse';
import styles from './ApplicationSentContent.module.scss';
import { getTranslation } from '../../utils/functions';
import { createTicketHolderModelMock, replaceMagicFields } from './helpers';
import ENDPOINTS, { ENDPOINTS_SEGMENTS } from '../../constants/endpoints';
import {
  formInitialValuesSelector,
  languageSelector,
  resultCodeSelector,
} from '../../store/selectors/appSelectors';
import {
  printLayoutIdSelector,
  projectIdSelector,
  submitResultScreensSelector,
  textsOverwritingsSelector,
} from '../../store/selectors/globalConfigSelectors';
import {
  accreditationRequestIdSelector,
  credentialProfileIdSelector,
  eventIdSelector,
} from '../../store/selectors/formSessionSelectors';
import { isAuthenticated } from '../../utils/authService';
import { isQueryParamsExist } from '../../utils/functions';
import { Typography } from '@hid-galaxy-ui/galaxy-react';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';

const useCardStyles = (isMobileView) =>
  makeStyles({
    card: {
      padding: isMobileView ? `24px 20px 60px 20px` : `40px 60px`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      marginTop: isMobileView ? '0px' : '60px',
    },
  });

const useButtonStyles = makeStyles((theme) => {
  return {
    root: {
      color: theme.palette.common.white,
      textTransform: 'uppercase',
      marginTop: '52px',
    },
  };
});

const useSuccessIconStyles = makeStyles(() => {
  return {
    img: {
      width: '120px',
    },
  };
});

const ApplicationSentContent = () => {
  const {
    resultCode,
    language,
    formInitialValues,
    textsOverwritings,
    submitResultScreens,
    printLayoutId,
    projectId,
    accreditationRequestId,
    credentialProfileId,
    eventId,
  } = useSelector((state) => ({
    // app state
    resultCode: resultCodeSelector(state),
    language: languageSelector(state),
    formInitialValues: formInitialValuesSelector(state),
    // globalConfig state
    textsOverwritings: textsOverwritingsSelector(state),
    submitResultScreens: submitResultScreensSelector(state),
    printLayoutId: printLayoutIdSelector(state),
    projectId: projectIdSelector(state),
    // formSessionState
    accreditationRequestId: accreditationRequestIdSelector(state),
    credentialProfileId: credentialProfileIdSelector(state),
    eventId: eventIdSelector(state),
  }));

  const PATHS = buildPATHS();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [detectChangeLanguage, setDetectChangeLanguage] = useState(true);
  const [resourceLocation, setResourceLocation] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useCardStyles(isMobileView)();
  const buttonStyles = useButtonStyles();
  const successIconStyles = useSuccessIconStyles();

  const ticketHolderMock = createTicketHolderModelMock({
    ...formInitialValues,
    credentialProfileId,
    projectId,
    eventId,
    accreditationRequestId,
  });

  const isSubmitResultScreens =
    submitResultScreens && submitResultScreens.length > 0;

  if (isAuthenticated() && !isSubmitResultScreens) {
    history.push(PATHS.my_forms);
    return;
  } else if (!isAuthenticated() && !isSubmitResultScreens) {
    history.push(PATHS.welcome);
    return;
  }

  const openSnackbar = () => {
    setSnackbarOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarOpen(false);
  };

  const editResponse = () => {
    batch(() => {
      dispatch(allActions.formConfigActions.resetAcceptedSteps());
      dispatch(allActions.formConfigActions.setFormStep(0));
      dispatch(allActions.appConfigActions.setApplicationSent(false));
      dispatch(allActions.appConfigActions.setAccessToRegister(true));
      dispatch(allActions.appConfigActions.setMessageFromBackend(false));
    });
    history.push(PATHS.register);
  };

  const backendResponseTemplate = submitResultScreens?.find(
    (responseType) =>
      responseType[BACKEND_RESPONSE_RESULT_SCREENS.code] === resultCode,
  );

  const getValueFromSubmitResultScreens = (key) =>
    backendResponseTemplate && backendResponseTemplate[key];

  const title = getTranslation(
    getValueFromSubmitResultScreens(BACKEND_RESPONSE_RESULT_SCREENS?.title),
  );
  const description = getTranslation(
    getValueFromSubmitResultScreens(
      BACKEND_RESPONSE_RESULT_SCREENS?.description,
    ),
  );
  const editButtonVisible = getValueFromSubmitResultScreens(
    BACKEND_RESPONSE_RESULT_SCREENS.editButtonVisible,
  );

  const getPDFResourceLocation = async (taskId) => {
    try {
      const url = ENDPOINTS.GET_PDF_RESOURCE_LOCATION.replace(
        ENDPOINTS_SEGMENTS.TASK_ID,
        taskId,
      );
      return Api.get(url, {});
    } catch (error) {
      openSnackbar(true);
      setDownloadingPDF(false);
      throw new Error(error);
    }
  };

  const getPDFBlob = async (taskId) => {
    try {
      const intervalId = setInterval(async () => {
        const res = await getPDFResourceLocation(taskId);
        if (res.status !== 'INPROGRESS') {
          setResourceLocation(res.resourceLocation);
          clearInterval(intervalId);
        }
      }, 600);
    } catch (error) {
      openSnackbar(true);
      setDownloadingPDF(false);
    }
  };

  const getPDFInstructions = async () => {
    try {
      const url = ENDPOINTS.GET_PDF_INSTRUCTIONS.replace(
        ENDPOINTS_SEGMENTS.PRINT_LAYOUT_ID,
        printLayoutId,
      );
      const { taskId } = await Api.post(url, ticketHolderMock, {});

      await getPDFBlob(taskId);
    } catch (error) {
      openSnackbar(true);
      setDownloadingPDF(false);
      throw new Error(error);
    }
  };

  const downloadTicket = () => {
    downloadFile(
      resourceLocation,
      `${formInitialValues.firstName.toLowerCase()}_${formInitialValues.lastName.toLowerCase()}_ticket.pdf`,
    );
    // downloadFile is synchronous — trigger state reset immediately after.
    setDownloadingPDF(false);
  };

  useEffect(() => {
    setDetectChangeLanguage(true);
  }, [language]);

  useEffect(() => {
    if (resourceLocation) {
      downloadTicket();
    }
  }, [resourceLocation]);

  return (
    <>
      {detectChangeLanguage && (
        <Card className={cx(styles.card, classes.card)}>
          <img
            src="assets/success_accr.png"
            alt="Success"
            className={successIconStyles.img}
          />
          <Typography variant={TypographyVariantEnum.H1}>
            {replaceMagicFields(title, formInitialValues)}
          </Typography>
          <p dangerouslySetInnerHTML={{ __html: sanitizer(description) }} />
          {editButtonVisible && (
            <Button
              color="primary"
              variant="contained"
              className={buttonStyles.root}
              onClick={() => {
                editResponse();
              }}
            >
              {getTranslation(textsOverwritings, 'BUTTON_SUMMARY_EDIT') ||
                t('BUTTON_SUMMARY_EDIT')}
            </Button>
          )}

          {printLayoutId && (
            <Button
              color="primary"
              variant="contained"
              className={buttonStyles.root}
              disabled={downloadingPDF}
              onClick={async () => {
                setDownloadingPDF(true);
                return resourceLocation
                  ? downloadTicket()
                  : getPDFInstructions();
              }}
            >
              {downloadingPDF ? (
                <CircularProgress
                  style={{ color: 'rgba(0,0,0,0.87)' }}
                  size={20}
                />
              ) : (
                <>
                  <GetAppIcon fontSize="small" style={{ marginRight: 8 }} />
                  {getTranslation(textsOverwritings, 'BUTTON_DOWNLOAD') ||
                    t('BUTTON_DOWNLOAD')}
                </>
              )}
            </Button>
          )}
        </Card>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleClose}
        message={
          getTranslation(textsOverwritings, 'AN_ERROR_OCCURED') ||
          t('AN_ERROR_OCCURED')
        }
        action={
          <>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
    </>
  );
};

export default ApplicationSentContent;
